import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import type { AiTierId } from '../../types/ai'
import { AI_TIER_CATALOG, lockedTierCopy, TIER_ORDER } from '../../utils/aiTierCatalog'
import { estimateAiCreditsForText } from '../../utils/aiBilling'
import { FloatingMenu } from '../Common/FloatingMenu'
import { Pressable } from '../Common/Pressable'
import { ProBadge } from '../Common'
import styles from './AiTierPickerButton.module.css'

/**
 * Claude/Codex-style tier picker. A compact trigger button lives in the
 * composer toolbar next to the `+`; clicking opens a popover listing all
 * three tiers (Line · Line Pro · Line Max) with their backing model name and
 * a live cost preview. Locked tiers show a padlock and fire onLockedClick
 * so the parent can open its upsell modal.
 *
 * All gating and bypass logic is owned by the parent via availableTiers /
 * lockedTiers — the picker just reflects state and reports intent.
 */
export interface AiTierPickerButtonProps {
  tier: AiTierId
  onChange: (next: AiTierId) => void
  availableTiers: readonly AiTierId[]
  lockedTiers: readonly AiTierId[]
  /** Composer text used to compute the live per-tier cost estimate. */
  composerContent?: string
  /** Human-readable plan name for locked-row tooltip copy. */
  planName?: string
  /** Fires when the user selects a locked tier. */
  onLockedClick?: (attempted: AiTierId) => void
  /** Optional low-credits warning glow on the trigger. */
  isLowCredits?: boolean
  disabled?: boolean
  ariaLabel?: string
}

const formatCredits = (credits: number): string => {
  if (credits < 1) return '<1 cr'
  if (credits < 1000) return `${Math.round(credits)} cr`
  return `${(credits / 1000).toFixed(1)}k cr`
}

export const AiTierPickerButton = ({
  tier,
  onChange,
  availableTiers,
  lockedTiers,
  composerContent = '',
  planName,
  onLockedClick,
  isLowCredits = false,
  disabled = false,
  ariaLabel = 'AI quality tier',
}: AiTierPickerButtonProps) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [focusIdx, setFocusIdx] = useState<number>(() => Math.max(0, TIER_ORDER.indexOf(tier)))
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([])

  const activeDescriptor = AI_TIER_CATALOG[tier]

  const costs = useMemo(() => {
    const trimmed = composerContent.trim()
    if (!trimmed) return null
    const record: Record<AiTierId, number> = { line: 0, line_pro: 0, line_max: 0 }
    for (const id of TIER_ORDER) {
      const model = AI_TIER_CATALOG[id].peakModel
      record[id] = estimateAiCreditsForText(model, trimmed).credits
    }
    return record
  }, [composerContent])

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    setFocusIdx(Math.max(0, TIER_ORDER.indexOf(tier)))
  }, [open, tier])

  useEffect(() => {
    if (!open) return
    const btn = rowRefs.current[focusIdx]
    btn?.focus()
  }, [open, focusIdx])

  const handleSelect = useCallback((candidate: AiTierId) => {
    if (lockedTiers.includes(candidate)) {
      onLockedClick?.(candidate)
      return
    }
    if (candidate !== tier) onChange(candidate)
    close()
  }, [close, lockedTiers, onChange, onLockedClick, tier])

  const handleListKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setFocusIdx((idx) => (idx + 1) % TIER_ORDER.length)
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setFocusIdx((idx) => (idx - 1 + TIER_ORDER.length) % TIER_ORDER.length)
      return
    }
    if (event.key === 'Home') {
      event.preventDefault()
      setFocusIdx(0)
      return
    }
    if (event.key === 'End') {
      event.preventDefault()
      setFocusIdx(TIER_ORDER.length - 1)
    }
  }, [])

  return (
    <div className={styles.root} data-floating-menu-root="true">
      <Pressable
        ref={triggerRef}
        type="button"
        className={[
          styles.trigger,
          isLowCredits ? styles.isLowCredits : '',
        ].filter(Boolean).join(' ')}
        data-tier={activeDescriptor.id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        title={`Line quality tier — ${activeDescriptor.longLabel}`}
      >
        <span className={styles.triggerDot} aria-hidden="true" />
        <span className={styles.triggerLabel}>{activeDescriptor.label}</span>
        <ExpandMoreRoundedIcon fontSize="small" className={styles.triggerChevron} />
      </Pressable>

      <FloatingMenu
        anchorRef={triggerRef}
        isOpen={open}
        onClose={close}
        placement="top-start"
        className={styles.popover}
        role="listbox"
        ariaLabel={ariaLabel}
      >
        <div className={styles.header}>
          <span className={styles.headerTitle}>Line quality tier</span>
          <span className={styles.headerSub}>Higher tiers cost more credits per prompt.</span>
        </div>
        <div
          className={styles.list}
          onKeyDown={handleListKeyDown}
        >
          {TIER_ORDER.map((id, idx) => {
            const descriptor = AI_TIER_CATALOG[id]
            const locked = lockedTiers.includes(id)
            const available = availableTiers.includes(id)
            const isCurrent = tier === id
            const cost = costs ? costs[id] : null
            const rowClass = [
              styles.row,
              isCurrent ? styles.isCurrent : '',
              locked ? styles.isLocked : '',
            ].filter(Boolean).join(' ')
            const tooltip = locked ? lockedTierCopy(id, planName ?? '') : descriptor.upsellPitch
            return (
              <Pressable
                key={id}
                ref={(el) => { rowRefs.current[idx] = el }}
                type="button"
                role="option"
                aria-selected={isCurrent}
                aria-disabled={locked ? true : undefined}
                data-tier={id}
                className={rowClass}
                title={tooltip}
                tabIndex={focusIdx === idx ? 0 : -1}
                onClick={() => handleSelect(id)}
                onFocus={() => setFocusIdx(idx)}
              >
                <span className={styles.rowDot} aria-hidden="true" />
                <span className={styles.rowMain}>
                  <span className={styles.rowLabel}>
                    <strong>{descriptor.label}</strong>
                    {locked && <LockRoundedIcon fontSize="inherit" className={styles.rowLock} aria-label="Locked" />}
                    {!locked && !available && <ProBadge label="Premium" className={styles.rowBadge} />}
                  </span>
                </span>
                <span className={styles.rowTrailing}>
                  {cost !== null && <span className={styles.rowCost}>{`≈ ${formatCredits(cost)}`}</span>}
                  {isCurrent && <CheckRoundedIcon fontSize="small" className={styles.rowCheck} aria-hidden="true" />}
                </span>
              </Pressable>
            )
          })}
        </div>
      </FloatingMenu>
    </div>
  )
}
